import { cn } from '@/lib/utils';
import type { TaskLabelView } from '@/lib/api/generated/model';

export interface HiddenLabelsCountProps {
  /** The labels left out of the row, in the order the row would show them. */
  labels: readonly Pick<TaskLabelView, 'name'>[];
  className?: string;
}

/**
 * "+2" for the labels a row had no room for, naming them.
 *
 * Named rather than decorative: a bare "+2" is a dead end for anyone who
 * cannot open the task to find out what it stands for.
 *
 * The name is carried by real text — visible "+2" hidden from the
 * accessibility tree, and the full list in an `sr-only` span beside it —
 * rather than by `aria-label`. ARIA forbids naming a role-less generic
 * element, so an `aria-label` on a bare `<span>` is not reliably exposed at
 * all. `title` gives sighted readers the same list on hover, which
 * `aria-label` never did.
 *
 * Renders nothing when nothing was left out.
 */
export function HiddenLabelsCount({
  labels,
  className,
}: HiddenLabelsCountProps) {
  if (labels.length === 0) return null;
  const names = labels.map((label) => label.name).join(', ');

  return (
    <span
      title={names}
      className={cn('text-muted-foreground text-xs font-medium', className)}
    >
      <span aria-hidden="true">+{labels.length}</span>
      <span className="sr-only">{`${String(labels.length)} more: ${names}`}</span>
    </span>
  );
}
